import { Button, CloseButton, Dialog, DialogBody, DialogContent, DialogFooter, Drawer, Field, Flex, Portal } from '@chakra-ui/react';
import { FormInput, Loading, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteUser, useGetSingleUser, useUpdateUser } from '../../api/hooks/useUserSuperadmin';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';
import { Select } from 'chakra-react-select';

type UpdateUserForm = {
	firstName: string;
	lastName: string;
	phone: string;
	userAccounts: number[];
};

export default function SuperadminUserDrawer({ userID }: { userID: number }) {
	const { t } = useTranslation();
	const { mutate, isPending: isUpdatingUser } = useUpdateUser();
	const { closeDrawer } = useDrawer();
	const [isRemoveOpen, setIsRemoveOpen] = useState<boolean>(false);
	const queryClient = useQueryClient();
	const { data: fetchedAccounts, isPending: isPendingAccounts } = useGetAllAccounts();

	const { data: user, isFetching: isFetchingUser } = useGetSingleUser(userID, {
		isEnabled: !!userID,
	});

	const methods = useForm<UpdateUserForm>({
		defaultValues: {
			firstName: user?.firstName || '',
			lastName: user?.lastName || '',
			phone: user?.phone || '',
			userAccounts:
				user?.userAccounts?.map(ua => {
					const account = fetchedAccounts?.data.accounts.find(ac => ac.accountID === ua.accountID);
					return account?.accountID;
				}) || [],
		},
	});

	const {
		control,
		setValue,
		handleSubmit,
		reset,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (user) {
			reset({
				firstName: user.firstName,
				lastName: user.lastName,
				phone: user.phone,
				userAccounts:
					user.userAccounts?.map(ua => {
						const account = fetchedAccounts?.data.accounts.find(ac => ac.accountID === ua.accountID);
						return account?.accountID;
					}) || [],
			});
		}
	}, [user, reset, fetchedAccounts]);

	function addAccounts(payload: Partial<SuperAdminUserUpdateRequest>, accountIDs: number[]): Partial<SuperAdminUserUpdateRequest> {
		const newUserAccounts: {
			userAccountID?: number;
			accountID?: number;
			delete?: boolean;
			userAccountRole?: UserAccountRole;
		}[] = [];

		const currentAccountIDs =
			user?.userAccounts?.map(ua => {
				const account = fetchedAccounts?.data.accounts.find(ac => ac.accountID === ua.accountID);
				return account?.accountID;
			}) ?? [];

		accountIDs.forEach(accountID => {
			const alreadyExists = currentAccountIDs.includes(accountID);
			if (!alreadyExists) {
				newUserAccounts.push({ accountID: accountID, userAccountRole: 'regular' });
			}
		});

		if (user?.userAccounts) {
			user.userAccounts.forEach(userAccount => {
				if (!accountIDs.includes(userAccount.accountID)) {
					newUserAccounts.push({
						userAccountID: userAccount.userAccountID,
						delete: true,
					});
				}
			});
		}

		return {
			...payload,
			userAccounts: newUserAccounts,
		};
	}

	function updateUser(values: UpdateUserForm): void {
		let payload: Partial<SuperAdminUserUpdateRequest> = {
			userID: userID,
		};

		(Object.keys(dirtyFields) as Array<keyof UpdateUserForm>).forEach(key => {
			const value = values[key];

			if (value !== undefined && key !== 'userAccounts') {
				payload[key] = value as string;
			}
		});

		payload = addAccounts(payload, values.userAccounts);
		if (payload.userAccounts && payload.userAccounts.length === 0) {
			delete payload.userAccounts;
		}

		mutate(payload, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.saved'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['users'] });
				queryClient.refetchQueries({ queryKey: ['account'] });
				queryClient.refetchQueries({ queryKey: ['user', payload.userID] });

				closeDrawer('userDrawer');
			},
		});
	}

	const userAccounts = useWatch({ control, name: 'userAccounts' });
	const firstName = useWatch({ control, name: 'firstName' });
	const lastName = useWatch({ control, name: 'lastName' });

	function getAccountStatusTranslation(status: AccountStatus): string {
		switch (status) {
			case 'waitingForApproval':
				return t('general.waiting_for_approval');
			case 'rejected':
				return t('general.rejected');
			case 'approved':
				return t('general.approved');
			case 'locked':
				return t('general.locked');
			default:
				return t('general.unknown');
		}
	}

	if (isPendingAccounts || isFetchingUser) {
		return <Loading />;
	}

	if (!fetchedAccounts) {
		throw new Error('Unable to fetch accounts.');
	}

	const accountsCollection = fetchedAccounts?.data.accounts?.map(account => {
		let label = account.name;
		if (account.accountStatus === 'rejected' || account.accountStatus === 'locked') {
			label = `${label} - ${getAccountStatusTranslation(account.accountStatus).toUpperCase()}`;
		}
		return {
			label: label,
			value: account.accountID,
		};
	});

	return isFetchingUser ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(updateUser)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>{`${firstName} ${lastName}`}</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
						<FormInput label={t('general.first_name')} name="firstName" maxLength={100} />
						<FormInput label={t('general.last_name')} name="lastName" maxLength={100} />
						<FormInput
							inputProps={{ style: { width: '100%' } }}
							helperText={t('general.phone_in_international_format')}
							helperTextProps={{ style: { textAlign: 'start' } }}
							type="tel"
							label={t('general.phone')}
							name="phone"
							maxLength={30}
						/>
						<Field.Root>
							<Field.Label>{t('general.account_access')}</Field.Label>
							<Select
								isMulti
								options={accountsCollection}
								value={userAccounts.map(userAccount => {
									const account = accountsCollection?.find(acc => acc.value === userAccount);
									return {
										label: account?.label,
										value: userAccount,
									};
								})}
								onChange={e => {
									const accounts = e.map(acc => acc.value);
									setValue('userAccounts', accounts, {
										shouldDirty: true,
									});
								}}
							></Select>
						</Field.Root>
					</Flex>
				</Drawer.Body>
				<Drawer.Footer>
					<RemoveUser userId={userID} isOpen={isRemoveOpen} setIsOpen={setIsRemoveOpen} />
					<Button id="removeUserButton" marginRight={'auto'} onClick={() => setIsRemoveOpen(true)} variant="subtle">
						{t('general.remove_from_account')}
					</Button>

					<Button onClick={() => closeDrawer('userDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingUser || !isDirty}>
						{isUpdatingUser ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}

function RemoveUser({ userId, isOpen, setIsOpen }: { userId: number; isOpen: boolean; setIsOpen: (value: boolean) => void }) {
	const { t } = useTranslation();
	const { closeDrawer } = useDrawer();
	const { mutate, isPending: isDeletingUser } = useDeleteUser();
	const queryClient = useQueryClient();

	function removeUser(): void {
		mutate(userId, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.removed'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['users'] });
				queryClient.refetchQueries({ queryKey: ['account'] });
				closeDrawer('userDrawer');
			},
		});
	}

	return (
		<Dialog.Root open={isOpen} onOpenChange={e => setIsOpen(e.open)} placement="center">
			<Dialog.Backdrop />
			<Portal>
				<Dialog.Positioner pointerEvents="none">
					<DialogContent>
						<Dialog.Header>
							<Dialog.Title>{t('general.remove_user')}</Dialog.Title>
						</Dialog.Header>
						<Dialog.CloseTrigger />
						<DialogBody>
							<p>{t('general.remove_user')}?</p>
						</DialogBody>
						<DialogFooter>
							<Dialog.ActionTrigger asChild>
								<Button variant="outline">{t('general.cancel')}</Button>
							</Dialog.ActionTrigger>
							<Button id="confirmRemoveUserButton" disabled={isDeletingUser} variant="subtle" onClick={removeUser}>
								{isDeletingUser ? t('general.please_wait') : t('general.remove')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
}
